import { useEffect, useState } from 'react';
import {
  FileBarChart,
  Archive,
  FileText,
  Layers,
  DollarSign,
  Route,
  X,
  FileDown,
  FileSpreadsheet,
  Search,
} from 'lucide-react';
import relatorioService, { TIPOS_RELATORIO, type RelatorioFiltros, type RelatorioFormato, type TipoRelatorio } from '@/services/relatorio';
import tipoProdutoService, { getTipoProdutoUuid, type TipoProduto } from '@/services/tipoProduto';
import loteService, { type Lote } from '@/services/lote';
import produtoService, { type Produto } from '@/services/produto';
import { Button } from '@/Components/ui/Button';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';

const ICONS: Record<string, typeof Archive> = {
  produtos: Archive,
  ordens: FileText,
  estoque: Layers,
  custos: DollarSign,
};
const CORES: Record<string, string> = {
  produtos: 'text-sky-600 bg-sky-50',
  ordens: 'text-amber-600 bg-amber-50',
  estoque: 'text-emerald-600 bg-emerald-50',
  custos: 'text-purple-600 bg-purple-50',
};

const STATUS_OPTIONS = [
  { value: 'ABERTA', label: 'Aberta' },
  { value: 'LIBERADA', label: 'Liberada' },
  { value: 'EM_PRODUCAO', label: 'Em Produção' },
  { value: 'PAUSADA', label: 'Pausada' },
  { value: 'ENCERRADA', label: 'Encerrada' },
  { value: 'CANCELADA', label: 'Cancelada' },
];

const LOTE_STATUS_OPTIONS = [
  { value: 'LIVRE', label: 'Livre' },
  { value: 'BLOQUEADO', label: 'Bloqueado' },
  { value: 'QUARENTENA', label: 'Quarentena' },
];

const REQUEREM_DATAS = ['custos'];

function getSearchPlaceholder(tipoId: string): string {
  if (tipoId === 'produtos') return 'Código, descrição, código ERP, barras ou NCM';
  if (tipoId === 'ordens') return 'Número da OP, lote ou produto';
  if (tipoId === 'custos') return 'Número da OP ou produto';
  return 'Produto, código, lote ou fornecedor';
}

type AutocompleteOption = {
  value: string;
  title: string;
  description?: string;
};

type AutocompleteFilterProps = {
  value?: string;
  options: AutocompleteOption[];
  placeholder: string;
  emptyLabel: string;
  onChange: (value: string) => void;
};

function AutocompleteFilter({ value, options, placeholder, emptyLabel, onChange }: AutocompleteFilterProps) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value);
  const [text, setText] = useState('');
  const termo = text.trim().toLowerCase();

  useEffect(() => {
    setText(selected ? `${selected.title}${selected.description ? ` - ${selected.description}` : ''}` : '');
  }, [selected]);

  const sugestoes = options
    .filter((option) => {
      if (!termo) return true;
      return `${option.title} ${option.description || ''}`.toLowerCase().includes(termo);
    })
    .slice(0, 12);

  return (
    <div className="nx-autocomplete-filter nx-report-autocomplete">
      <div className="nx-search-field nx-search-field--filter nx-autocomplete-filter__control">
        <Search size={15} strokeWidth={2} aria-hidden />
        <input
          type="search"
          value={text}
          placeholder={placeholder}
          onChange={(event) => {
            setText(event.target.value);
            if (value) onChange('');
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 140)}
        />
        {text ? (
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setText('');
              onChange('');
            }}
            aria-label="Limpar filtro"
          >
            ×
          </button>
        ) : null}
      </div>
      {open ? (
        <div className="nx-autocomplete-filter__menu">
          {sugestoes.length === 0 ? (
            <div className="nx-autocomplete-filter__empty">{emptyLabel}</div>
          ) : (
            sugestoes.map((option) => (
              <button
                type="button"
                key={option.value}
                className={option.value === value ? 'is-selected' : undefined}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(option.value);
                  setText(`${option.title}${option.description ? ` - ${option.description}` : ''}`);
                  setOpen(false);
                }}
              >
                <strong>{option.title}</strong>
                {option.description ? <span>{option.description}</span> : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function RelatoriosPage() {
  const [tiposProduto, setTiposProduto] = useState<TipoProduto[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [dialog, setDialog] = useState(false);
  const [selecionado, setSelecionado] = useState<TipoRelatorio | null>(null);
  const [gerando, setGerando] = useState<RelatorioFormato | null>(null);
  const [aviso, setAviso] = useState('');

  const [filtros, setFiltros] = useState<RelatorioFiltros>({});
  const tiposProdutoValidos = tiposProduto.filter((tipo) => getTipoProdutoUuid(tipo));
  const tipoProdutoOptions = tiposProdutoValidos.map((tipo) => ({
    value: getTipoProdutoUuid(tipo) || '',
    title: tipo.codigo || '-',
    description: tipo.descricao,
  }));
  const produtoOptions = produtos.map((produto) => ({
    value: produto.id,
    title: produto.codigo || '-',
    description: produto.descricao,
  }));

  const [dialogRastreabilidade, setDialogRastreabilidade] = useState(false);
  const [buscaLote, setBuscaLote] = useState('');
  const [lotesEncontrados, setLotesEncontrados] = useState<Lote[]>([]);
  const [gerandoRastreabilidade, setGerandoRastreabilidade] = useState(false);

  useEffect(() => {
    tipoProdutoService.listar().then(setTiposProduto).catch(console.error);
    produtoService.listar().then(setProdutos).catch(console.error);
  }, []);

  const abrirModal = (tipo: TipoRelatorio) => {
    setSelecionado(tipo);
    setFiltros({});
    setAviso('');
    setDialog(true);
  };

  const gerar = async (formato: RelatorioFormato) => {
    if (!selecionado) return;

    if (REQUEREM_DATAS.includes(selecionado.id) && (!filtros.data_inicio || !filtros.data_fim)) {
      setAviso('Informe as datas de início e fim para gerar este relatório.');
      return;
    }

    setAviso('');
    setGerando(formato);
    try {
      await relatorioService.baixar(selecionado.id, formato, filtros);
      setDialog(false);
    } catch (error) {
      console.error('Erro ao gerar relatório', error);
      setAviso('Erro ao gerar relatório. Tente novamente.');
    } finally {
      setGerando(null);
    }
  };

  const buscarLotes = async () => {
    if (!buscaLote) return;
    try {
      setLotesEncontrados(await loteService.listar({ search: buscaLote }));
    } catch (error) {
      console.error('Erro ao buscar lotes', error);
    }
  };

  const gerarRastreabilidadeLote = async (lote: Lote) => {
    setGerandoRastreabilidade(true);
    try {
      await relatorioService.baixarRastreabilidadeLote(lote.id, lote.numero_lote);
      setDialogRastreabilidade(false);
    } catch (error) {
      console.error('Erro ao gerar relatório de rastreabilidade', error);
    } finally {
      setGerandoRastreabilidade(false);
    }
  };

  return (
    <div className="rounded-lg bg-white p-5 shadow-sm">
      <div className="mb-5 flex items-center gap-2">
        <FileBarChart className="h-7 w-7 text-sky-600" />
        <h2 className="text-xl font-bold text-slate-800">Relatórios</h2>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {TIPOS_RELATORIO.map((tipo) => {
          const Icon = ICONS[tipo.id] || FileText;
          return (
            <button
              key={tipo.id}
              type="button"
              onClick={() => abrirModal(tipo)}
              className="flex flex-col items-center gap-2 rounded-lg border border-slate-200 p-5 text-center transition hover:border-sky-300 hover:shadow-md"
            >
              <span className={`flex h-14 w-14 items-center justify-center rounded-full ${CORES[tipo.id]}`}>
                <Icon className="h-7 w-7" />
              </span>
              <h4 className="text-sm font-semibold text-slate-800">{tipo.titulo}</h4>
            </button>
          );
        })}

        <button
          type="button"
          onClick={() => { setDialogRastreabilidade(true); setBuscaLote(''); setLotesEncontrados([]); }}
          className="flex flex-col items-center gap-2 rounded-lg border border-slate-200 p-5 text-center transition hover:border-sky-300 hover:shadow-md"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-cyan-50 text-cyan-600">
            <Route className="h-7 w-7" />
          </span>
          <h4 className="text-sm font-semibold text-slate-800">Rastreabilidade de Lote</h4>
        </button>
      </div>

      {dialog && selecionado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="nx-report-dialog">
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <h3 className="text-base font-semibold">{selecionado.titulo}</h3>
              <button type="button" onClick={() => setDialog(false)} className="rounded p-1 text-slate-400 hover:bg-slate-100">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="nx-report-body">
              <p className="text-sm text-slate-500">Configure os filtros para gerar o relatório:</p>

              {selecionado.filtros.includes('search') && (
                <div className="nx-report-field">
                  <label>Buscar</label>
                  <Input
                    value={filtros.search || ''}
                    onChange={(e) => setFiltros({ ...filtros, search: e.target.value })}
                    placeholder={getSearchPlaceholder(selecionado.id)}
                    className="nx-report-control"
                  />
                </div>
              )}

              {selecionado.filtros.includes('tipo_produto') && (
                <div className="nx-report-field">
                  <label>Tipo de Produto</label>
                  <AutocompleteFilter
                    value={filtros.tipo_produto || ''}
                    options={tipoProdutoOptions}
                    placeholder="Buscar tipo de produto..."
                    emptyLabel="Nenhum tipo encontrado"
                    onChange={(tipo_produto) => setFiltros({ ...filtros, tipo_produto })}
                  />
                </div>
              )}

              {selecionado.filtros.includes('produto') && (
                <div className="nx-report-field">
                  <label>Produto</label>
                  <AutocompleteFilter
                    value={filtros.produto || ''}
                    options={produtoOptions}
                    placeholder="Buscar produto..."
                    emptyLabel="Nenhum produto encontrado"
                    onChange={(produto) => setFiltros({ ...filtros, produto })}
                  />
                </div>
              )}

              {selecionado.filtros.includes('ativo') && (
                <div className="nx-report-field">
                  <label>Situação</label>
                  <Select
                    value={filtros.ativo ?? ''}
                    onChange={(e) => setFiltros({ ...filtros, ativo: e.target.value })}
                    className="nx-report-control"
                  >
                    <option value="">Todos</option>
                    <option value="true">Ativos</option>
                    <option value="false">Inativos</option>
                  </Select>
                </div>
              )}

              {selecionado.filtros.includes('status') && (
                <div className="nx-report-field">
                  <label>Status</label>
                  <Select
                    value={filtros.status || ''}
                    onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
                    className="nx-report-control"
                  >
                    <option value="">Todos</option>
                    {(selecionado.id === 'estoque' ? LOTE_STATUS_OPTIONS : STATUS_OPTIONS).map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </Select>
                </div>
              )}

              {selecionado.filtros.includes('localizacao') && (
                <div className="nx-report-field">
                  <label>Localização</label>
                  <Input
                    value={filtros.localizacao || ''}
                    onChange={(e) => setFiltros({ ...filtros, localizacao: e.target.value })}
                    placeholder="Localização do lote"
                    className="nx-report-control"
                  />
                </div>
              )}

              {selecionado.filtros.includes('data') && (
                <div className="nx-report-date-grid">
                  <div className="nx-report-field">
                    <label>Data Início</label>
                    <Input
                      type="date"
                      value={filtros.data_inicio || ''}
                      onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })}
                      className="nx-report-control"
                    />
                  </div>
                  <div className="nx-report-field">
                    <label>Data Fim</label>
                    <Input
                      type="date"
                      value={filtros.data_fim || ''}
                      onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })}
                      className="nx-report-control"
                    />
                  </div>
                </div>
              )}

              {aviso && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">{aviso}</p>}
            </div>
            <div className="nx-report-footer">
              <Button type="button" variant="ghost" onClick={() => setDialog(false)}>
                Cancelar
              </Button>
              {selecionado.formatos.includes('pdf') && (
                <Button
                  type="button"
                  onClick={() => gerar('pdf')}
                  variant="danger"
                  loading={gerando === 'pdf'}
                  disabled={gerando !== null}
                  loadingText="Gerando..."
                >
                  <FileDown size={16} />
                  PDF
                </Button>
              )}
              {selecionado.formatos.includes('excel') && (
                <Button
                  type="button"
                  onClick={() => gerar('excel')}
                  loading={gerando === 'excel'}
                  disabled={gerando !== null}
                  loadingText="Gerando..."
                >
                  <FileSpreadsheet size={16} />
                  Excel
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {dialogRastreabilidade && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <h3 className="flex items-center gap-2 text-base font-semibold">
                <Route className="h-4 w-4 text-cyan-600" />
                Rastreabilidade de Lote (Excel)
              </h3>
              <button type="button" onClick={() => setDialogRastreabilidade(false)} className="rounded p-1 text-slate-400 hover:bg-slate-100">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 p-4">
              <p className="text-sm text-slate-500">Busque o lote para gerar o histórico de inspeções de qualidade em Excel:</p>
              <div className="flex gap-2">
                <input
                  value={buscaLote}
                  onChange={(e) => setBuscaLote(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && buscarLotes()}
                  placeholder="Número do lote"
                  className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
                <button type="button" onClick={buscarLotes} className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700">
                  Buscar
                </button>
              </div>
              {lotesEncontrados.length > 0 && (
                <ul className="max-h-56 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200">
                  {lotesEncontrados.map((lote) => (
                    <li key={lote.id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span>
                        <span className="font-medium">{lote.numero_lote}</span>
                        <span className="ml-2 text-xs text-slate-400">{lote.produto_descricao || lote.produto_nome}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => gerarRastreabilidadeLote(lote)}
                        disabled={gerandoRastreabilidade}
                        className="flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                      >
                        <FileSpreadsheet className="h-3.5 w-3.5" />
                        Excel
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
